import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  AlertCompletion,
  AlertNestedCoCNote,
  AlertNestedSms,
  ChainOfCustodyItem,
  Reply,
} from '@/apis/types';

dayjs.extend(customParseFormat);

const BACKEND_TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function parseBackendTimestamp(timestamp: string): number {
  if (!timestamp) {
    return 0;
  }
  const parsed = dayjs(timestamp, BACKEND_TIMESTAMP_FORMAT, true);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

export type MergedTimelineItem =
  | { type: 'reply'; data: Reply; timestamp: string }
  | { type: 'completion'; data: AlertCompletion; timestamp: string }
  | { type: 'automation_sms'; data: AlertNestedSms; timestamp: string }
  | {
      type: 'automation_coc_note';
      data: AlertNestedCoCNote;
      timestamp: string;
    };

export function formatAssignmentDetail(completion: AlertCompletion): string {
  const ownerDetail = completion.selection_labels?.length
    ? `@${completion.selection_labels.join(', ')}`
    : '';
  return [ownerDetail, completion.tag_label].filter(Boolean).join(' · ');
}

/** Wrap a comment in curly quotes, inside its outer tag when it has one. */
export function quoteCommentHtml(html: string): string {
  const trimmedHtml = html.trim();
  if (!trimmedHtml) {
    return '';
  }

  const openingTag = trimmedHtml.match(/^<([a-z][\w-]*)(?:\s[^>]*)?>/i);
  const closingTag = trimmedHtml.match(/<\/([a-z][\w-]*)>\s*$/i);

  if (openingTag && closingTag && openingTag[1] === closingTag[1]) {
    const contentStart = openingTag[0].length;
    const contentEnd = trimmedHtml.length - closingTag[0].length;

    return `${trimmedHtml.slice(0, contentStart)}“${trimmedHtml.slice(
      contentStart,
      contentEnd
    )}”${trimmedHtml.slice(contentEnd)}`;
  }

  return `“${trimmedHtml}”`;
}

/** A person's own Resolve or Dismiss, not an AI or system one. */
export function isManualCloseEntry(completion: AlertCompletion): boolean {
  if (completion.activity_type || completion.ai_type) return false;
  // Logiblocks automations close alerts too, but nobody typed that comment.
  if (/by logiblocks/i.test(completion.title ?? '')) return false;
  if (completion.status !== 'Resolved' && completion.status !== 'Dismissed') {
    return false;
  }
  const description = (completion.status_info?.description ?? '').toLowerCase();
  return (
    description !== 'resolved via ai' &&
    !description.startsWith('dismissed due to')
  );
}

/** What a manual Resolve / Dismiss entry shows under its title. */
export function getCloseDetailParts(completion: AlertCompletion): {
  tag: string;
  comment: string;
} {
  return {
    tag: (completion.tag_label ?? '').trim(),
    comment: (completion.summary || completion.comment || '').trim(),
  };
}

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * `Tag · “comment”` as one line of HTML (comments can hold mentions), or
 * whichever of the two exists. When the comment is wrapped in a tag such as
 * `<p>`, the tag goes inside it so both stay on the same line.
 */
export function formatCloseDetailHtml(completion: AlertCompletion): string {
  const { tag, comment } = getCloseDetailParts(completion);
  // The tag stays in the primary text colour; the comment is the lighter one.
  const safeTag = tag
    ? `<span style="color: var(--text-primary)">${escapeHtml(tag)}</span>`
    : '';
  const quoted = quoteCommentHtml(comment);
  if (!safeTag) return quoted;
  if (!quoted) return safeTag;

  const openingTag = quoted.match(/^<([a-z][\w-]*)(?:\s[^>]*)?>/i);
  return openingTag
    ? `${openingTag[0]}${safeTag} · ${quoted.slice(openingTag[0].length)}`
    : `${safeTag} · ${quoted}`;
}

// Replies and automation SMS carry ids; completions do not, so they are keyed
// by when they happened and their title.
/** Stable React key for an item in the merged timeline. */
export const mergedTimelineItemKey = (item: MergedTimelineItem): string =>
  item.type === 'completion'
    ? `completion-${item.data.email?.message_id ?? `${item.timestamp}-${item.data.title}`}`
    : `${item.type}-${item.data.id ?? item.timestamp}`;

export interface MergeOptions {
  /**
   * Treat every email entry as an old one (only the latest inbound email
   * shows). Views a user cannot reply from, such as the public share page, set
   * this so the per-email entries never appear there.
   */
  ignoreEmailEntries?: boolean;
}

export function getMergedTimelineItems(
  custody: ChainOfCustodyItem,
  options: MergeOptions = {}
): MergedTimelineItem[] {
  const mappedReplies = (custody.replies || []).map((reply) => ({
    type: 'reply' as const,
    data: reply,
    timestamp: reply.timestamp,
  }));

  const mappedAutomationSms = (custody.automation_sms || []).map((sms) => ({
    type: 'automation_sms' as const,
    data: sms,
    timestamp: sms.timestamp,
  }));

  const mappedAutomationCoCNotes = (custody.automation_coc_notes || []).map(
    (note) => ({
      type: 'automation_coc_note' as const,
      data: note,
      timestamp: note.timestamp,
    })
  );

  const allCompletions = (custody.alert_completion || []).map((completion) => ({
    type: 'completion' as const,
    data: completion,
    timestamp: completion.timestamp ?? '',
  }));

  const isInboundEmail = (item: (typeof allCompletions)[number]): boolean =>
    item.data.ai_type === 'email' &&
    item.data.event === 'Inbound email received';

  // Rows that carry `email` come from the per-email entries (reply-to-AI-emails
  // contract): every one is its own entry. Older rows keep the existing rule of
  // showing only the latest inbound email.
  const isPerEmailEntry = (item: (typeof allCompletions)[number]): boolean =>
    !options.ignoreEmailEntries && Boolean(item.data.email);
  const legacyInboundItems = allCompletions.filter(
    (item) => isInboundEmail(item) && !isPerEmailEntry(item)
  );
  const otherCompletions = allCompletions.filter(
    (item) => !isInboundEmail(item) || isPerEmailEntry(item)
  );
  const dedupedInbound =
    legacyInboundItems.length > 0
      ? [
          legacyInboundItems.reduce((latest, item) =>
            parseBackendTimestamp(item.timestamp) >
            parseBackendTimestamp(latest.timestamp)
              ? item
              : latest
          ),
        ]
      : [];

  // Newest first. Timestamps are whole seconds, so items from the same second tie,
  // and the stable sort keeps this order for them: an automation note or SMS is
  // usually a workflow's reaction to the email or reply beside it, so it goes above.
  return [
    ...mappedAutomationCoCNotes,
    ...mappedAutomationSms,
    ...mappedReplies,
    ...otherCompletions,
    ...dedupedInbound,
  ].sort(
    (a, b) =>
      parseBackendTimestamp(b.timestamp) - parseBackendTimestamp(a.timestamp)
  );
}
